import {
  MultiSelectWithTag,
  Toggle,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useId, useState, type ComponentProps } from "react";

type SelectionProps = ComponentProps<typeof MultiSelectWithTag>;

/** Dropdown configuration plus the labels and toggle for the automation section. */
export interface AutomationProps extends Omit<
  SelectionProps,
  "options" | "className" | "open" | "onOpenChange"
> {
  options?: SelectionProps["options"];
  /** Emit the selected option IDs whenever the selection changes. */
  onChange?: SelectionProps["onChange"];
  title?: string;
  agentModeLabel?: string;
  selectionLabel?: string;
  /** Configure the toggle's ID, initial value, label, and change callback. */
  toggleProps?: Partial<ComponentProps<typeof Toggle>>;
  className?: string;
  selectionClassName?: string;
}

const DEFAULT_OPTIONS: SelectionProps["options"] = [
  { id: "inaccuracy", label: "Inaccuracy" },
  { id: "prescription-error", label: "Prescription Error" },
  { id: "insurance-error", label: "Insurance Error" },
  { id: "form-error", label: "Form Error" },
  { id: "in-progress", label: "In Progress" },
];

const Automation = ({
  options = DEFAULT_OPTIONS,
  title = "Automation",
  agentModeLabel = "Agent Mode (Agentic)",
  selectionLabel = "Enable Agentic For",
  toggleProps,
  className,
  selectionClassName,
  defaultValue = [],
  value,
  onChange,
  placeholder = "Select filters",
  hideFooter = true,
  disabled = false,
  targetRef,
  ...selectionProps
}: AutomationProps) => {
  const modalTargetRef = useModalPortalTarget();
  const toggleId = useId();
  const [isAgentModeEnabled, setIsAgentModeEnabled] = useState(
    toggleProps?.defaultValue ?? false,
  );
  const [selectedValues, setSelectedValues] = useState(defaultValue);

  useEffect(() => {
    setIsAgentModeEnabled(toggleProps?.defaultValue ?? false);
  }, [toggleProps?.defaultValue]);

  return (
    <div
      className={["automation-container", className].filter(Boolean).join(" ")}
    >
      <div className="title mb-2 text-x-tiny font-semibold uppercase leading-5 tracking-[0.0375rem] text-primaryGray-9">
        {title}
      </div>
      <fieldset
        disabled={disabled}
        className="toggle-selection mb-5 flex h-9 min-w-0 items-center justify-between rounded-md border border-primaryGray-15 px-3"
      >
        <label
          htmlFor={toggleProps?.id ?? toggleId}
          className="toggle-mode-text text-x-tiny font-semibold text-primaryGray-1"
        >
          {agentModeLabel}
        </label>
        <div className="toggler">
          <Toggle
            label=""
            {...toggleProps}
            id={toggleProps?.id ?? toggleId}
            defaultValue={isAgentModeEnabled}
            onChange={(data) => {
              // Turning the mode off hides the field but preserves selections for re-enabling.
              setIsAgentModeEnabled(data.value);
              toggleProps?.onChange?.(data);
            }}
          />
        </div>
      </fieldset>
      <div
        className={[
          "automation-selection",
          isAgentModeEnabled ? "automation-selection--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden={!isAgentModeEnabled}
      >
        <fieldset
          className="automation-selection__content"
          disabled={!isAgentModeEnabled || disabled}
        >
          <div className="card-title mb-2 text-xs font-semibold text-primaryGray-1">
            {selectionLabel}
          </div>
          <div className="select-multi-tag">
            <MultiSelectWithTag
              {...selectionProps}
              // Dismiss the portaled dropdown when mode changes, retaining selection above.
              key={isAgentModeEnabled ? "enabled" : "disabled"}
              options={options}
              value={value ?? selectedValues}
              onChange={(selectedIds) => {
                setSelectedValues(selectedIds);
                onChange?.(selectedIds);
              }}
              placeholder={placeholder}
              targetRef={targetRef ?? modalTargetRef}
              disabled={!isAgentModeEnabled || disabled}
              className={selectionClassName}
            />
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default Automation;
